import { useTranslation } from "react-i18next";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { CompanyPicker } from "./CompanyPicker";

interface RequireCompanyProps {
  children: React.ReactNode;
}

/**
 * Gates the app shell:
 * - loading → spinner
 * - 0 memberships → "contact your admin"
 * - >1 memberships and no active selection → company picker
 * - otherwise → app
 */
export function RequireCompany({ children }: RequireCompanyProps) {
  const { t } = useTranslation();
  const { id, loading, memberships, needsSelection, setActive } = useCurrentCompany();
  const { logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (needsSelection) {
    return <CompanyPicker memberships={memberships} onSelect={setActive} />;
  }

  if (!id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-semibold">{t("company.notAssigned")}</h1>
          <p className="text-muted-foreground">{t("company.notAssignedHelp")}</p>
          <Button variant="outline" onClick={() => logout()}>
            {t("auth.signOut")}
          </Button>
        </div>
      </div>
    );
  }


  return <>{children}</>;
}
